import PopupSectionRow from 'components/popup/PopupSectionRow'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePickPopup from '../popups/eventInfo/DatePickPopup'
import { useCalendar } from 'components/providers/CalendarProvider'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { IEventDetails } from 'hooks/useEventDetails'
import { Box, ClickAwayListener } from '@mui/material'
import { is24meApp } from 'utils/appType'
import { Controller } from 'react-hook-form'
import { CloseIcon } from 'assets/icons'
import { DocType } from 'types/api/changes'
import { getDefaultTimezone } from 'index'
import Logger from 'services/Logger'
import { TimePickerBox, buildTimeOptions } from './TimePickerBox'
import { isSameDay, isToday } from 'date-fns'

export enum PickerType {
  START,
  END
}

export interface DatePickerProps {
  pickerType: PickerType
  allDay: boolean
  setStartTime: (date: number) => void
  startTime: number
  setEndTime: (date: number) => void
  endTime: number
  timeZone: string | null | undefined
  setTimeZone: (tz?: string | undefined | null) => void
  duration: number
  eventHook: IEventDetails
  shouldShowTimeDropdown?: boolean
  dateFormat?: (timestamp: number) => string
  timeFormat?: (timestamp: number) => string
  hideDate?: boolean
  hideTime?: boolean
  readOnly?: boolean
}

let utc = require('dayjs/plugin/utc')
let timezone = require('dayjs/plugin/timezone') // dependent on utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)

export function EventDatePicker(props: DatePickerProps) {
  const pickerType = props.pickerType
  const {
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    allDay,
    timeZone,
    setTimeZone,
    shouldShowTimeDropdown,
    duration,
    eventHook,
    readOnly
  } = props

  Logger.blue('EventDatePicker render', {
    startTime,
    endTime,
    allDay,
    timeZone
  })

  const tz =
    eventHook.timeZone && eventHook.timeZone?.length > 0
      ? eventHook.timeZone
      : getDefaultTimezone()

  let zonedStart = dayjs(startTime).tz(tz)
  let zonedEnd = dayjs(endTime).tz(tz)

  if (allDay) {
    zonedStart = dayjs(startTime)
    zonedEnd = dayjs(endTime)
  }

  const [dateState, setDateState] = useState(false)

  const openDate = () => {
    setDateState(true)
  }

  const calendar = useCalendar()

  let dayjsTime = pickerType === PickerType.START ? zonedStart : zonedEnd

  const [time, setTime] = useState<Dayjs | null>(dayjsTime)

  useEffect(() => {
    if (props.eventHook.getValues('someday') === '0') {
      setTime(pickerType === PickerType.START ? zonedStart : zonedEnd)
    }
  }, [startTime, endTime])

  const onAcceptDate = (date: Dayjs | null, time?: boolean) => {
    setDateState(false)
    let newDate = date?.tz(props.eventHook.timeZone) ?? dayjs()

    newDate = (pickerType === PickerType.START ? zonedStart : zonedEnd)
      .set('hour', newDate.hour())
      .set('minute', newDate.minute())
      .set('date', newDate.date())
      .set('month', newDate.month())
      .set('year', newDate.year())

    if (allDay) {
      newDate = date ?? dayjs()
    }

    setTime(newDate)

    if (pickerType === PickerType.START) {
      const dayVisible =
        newDate.isAfter(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_FROM)).startOf('day')
        ) &&
        newDate.isBefore(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_TO))
            .subtract(1, 'day')
            .endOf('day')
        )
      if (props.eventHook.isNewEvent) {
        if (
          newDate.isAfter(dayjs(calendar.toISO)) ||
          newDate.isBefore(dayjs(calendar.fromISO))
        ) {
          calendar.onGoToDate(newDate.format('YYYY-MM-DD'))
        }
        calendar.onScrollToTime(newDate)
      }

      setStartTime(newDate.valueOf())
      setTimeout(
        () => {
          if (allDay || !props.eventHook.isNewEvent) return
          try {
            ;(calendar.ref as any)?.current.getApi().scrollToTime(
              dayjs(newDate)
                .tz(timeZone ?? getDefaultTimezone())
                .format('HH:mm:ss')
            )
          } catch (e) {
            //can't go to date
          }
        },
        !dayVisible ? 250 : 1
      )
    } else if (pickerType === PickerType.END) {
      console.log(newDate)
      setEndTime(newDate.valueOf())
    }
  }

  const { t } = useTranslation()

  const [showTimeMinutesPopup, setShowTimeMinutesPopup] = useState(false)

  function buildMinutes() {
    return buildTimeOptions(
      pickerType === PickerType.START
        ? zonedStart.startOf('day').subtract(30, 'minute')
        : zonedEnd,
      undefined,
      false,
      true,
      pickerType === PickerType.END,
      pickerType === PickerType.END ? zonedStart : undefined,
      tz
    )
  }

  // Inside your component:
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const longDateFormat =
    (props.dateFormat
      ? props.dateFormat(dayjsTime.valueOf())
      : dayjsTime.format('ll HH:mm')
    ).length > 11

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      id={'event-date-picker-box'}
      gap={2}
    >
      {!props.hideDate && (
        <DatePickPopup
          onAccept={onAcceptDate}
          value={startTime === 0 ? dayjs() : dayjsTime}
          open={dateState}
          setOpen={setDateState}
          eventHook={props.eventHook}
          instanceStart={
            pickerType === PickerType.START ? props.startTime : props.endTime
          }
        >
          <div
            id={
              pickerType === PickerType.START
                ? 'start-date-picker'
                : 'end-date-picker'
            }
            style={{
              pointerEvents: readOnly ? 'none' : 'auto',
              cursor: 'pointer',
              width: 'auto',
              flexWrap: 'nowrap',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
            onClick={openDate}
          >
            {props.eventHook.getValues('someday') === '1' ? (
              <PopupSectionRow
                actionBg
                onlyTitle
                clickableTitle
                engulfEntireRow
                gap="0rem"
                title={t('clickToSet')}
              />
            ) : (
              <PopupSectionRow
                actionBg
                onlyTitle
                clickableTitle
                gap="0rem"
                action={() => {
                  //
                }}
                title={
                  <Box
                    onClick={() => {}}
                    sx={{
                      width: 'fit-content',
                      borderBottom: readOnly
                        ? undefined
                        : props.dateFormat
                          ? '1px solid #E4E4E4'
                          : 'none'
                    }}
                  >
                    {props.dateFormat
                      ? props.dateFormat(dayjsTime.valueOf())
                      : `${dayjsTime.format('ll')}`}
                  </Box>
                }
              />
            )}
            {is24meApp() &&
              (props.eventHook.getValues('someday') ?? '0') === '0' && (
                <Controller
                  name="Type"
                  control={props.eventHook.control}
                  defaultValue={props.eventHook.type}
                  render={({ field }) => {
                    if (field.value !== DocType.TASK) return <></>

                    return (
                      <CloseIcon
                        onClick={(e) => {
                          e.stopPropagation()
                          props.eventHook.setSomeday('1')
                        }}
                      />
                    )
                  }}
                />
              )}
          </div>
        </DatePickPopup>
      )}
      {props.eventHook.getValues('someday') !== '1' &&
        !allDay &&
        !props.hideTime && (
          <Box
            id="time-picker-box"
            sx={{
              alignItems: 'center',
              display: 'flex'
            }}
          >
            <Controller
              name="someday"
              control={eventHook.control}
              defaultValue={eventHook.someday}
              render={({ field }) => {
                if (field.value === '1') return <></>

                return (
                  <TimePickerBox
                    readonly={props.readOnly}
                    allDay={allDay}
                    dayjsTime={dayjsTime}
                    timeFormat={props.timeFormat}
                    shouldShowTimeDropdown={shouldShowTimeDropdown}
                    showTimeMinutesPopup={showTimeMinutesPopup}
                    setShowTimeMinutesPopup={setShowTimeMinutesPopup}
                    buildMinutes={buildMinutes}
                    onAcceptDate={onAcceptDate}
                    setTime={setTime}
                    timeoutRef={timeoutRef}
                    withUnderline={!props.readOnly}
                    tz={tz}
                    isStartPicker={pickerType === PickerType.START}
                    shouldScrollToTime={pickerType === PickerType.START}
                  />
                )
              }}
            />
          </Box>
        )}
    </Box>
  )
}
